import React, { useState, useMemo, ReactNode, useEffect } from 'react';
import cx from 'classnames';
import {
  Button,
  ButtonVariant,
  ButtonShape,
} from '@vimily/bonjoro-ui-components-v2/Button';
import PublishModal from '~/App/Modules/Testimonials/Pages/Publish/Editor/Containers/PublishModal';

import {
  ToggleSelector,
  ToggleSelectorShape,
} from '@vimily/bonjoro-ui-components-v2/ToggleSelector';

import { Checkbox } from '@vimily/bonjoro-ui-components-v2/Checkbox';
import {
  DropdownSelector,
  SelectorType,
} from '@vimily/bonjoro-ui-components-v2/DropdownSelector';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTag, faFolder } from '@fortawesome/free-solid-svg-icons';
import { useTags } from '~/App/Api/Tag/hooks';
import { usePublishPageContext } from '~/App/Modules/Testimonials/Pages/Publish/Editor/Contexts/PublishPageContext';
import useFilteredTags from '~/App/Modules/Tags/Components/TagFilter/hooks/useFilteredTags';
import OrderTestimonialsModal from '../../../../../Components/OrderTestimonialsModal';
import { PublishSelectModalContextProvider } from '~/App/Modules/Testimonials/Pages/Publish/Editor/Contexts/PublishSelectModalContext';
import { usePublishEditorState } from '~/App/Modules/Testimonials/Pages/Publish/Editor/Contexts/PublishEditorContext';
import {
  useUpdatePublishable,
  queryKeys,
  parsePublishStateToMetadata,
} from '~/App/Api/Publishable/api';
import { useQueryClient } from 'react-query';
import useAnalytics from '~/react-hooks/use-analytics';
import {
  TESTIMONIAL_PUBLISH_SELECT_CHANGE_ORDER,
  TESTIMONIAL_PUBLISH_SELECT_CHANGE_SELECTION,
  TESTIMONIAL_PUBLISH_SELECT_TESTIMONIALS,
  TESTIMONIAL_PUBLISH_SELECT_TESTIMONIALS_FILTER,
} from '~/generated/AnalyticsTypes';

export interface SelectProps {
  onSelect: () => void;
}

interface SelectButtonProps {
  onSelect: () => void;
}

interface ChangeSelectionButtonProps {
  onChangeSelection: () => void;
}

const SelectButton = ({ onSelect }: SelectButtonProps) => {
  return (
    <Button
      variant={ButtonVariant.Primary}
      shape={ButtonShape.Rectangle}
      className='text-base mt-5'
      onClick={onSelect}
    >
      Select
    </Button>
  );
};

const ChangeSelectionButton = ({
  onChangeSelection,
}: ChangeSelectionButtonProps) => {
  return (
    <Button
      variant={ButtonVariant.SecondaryWhite}
      shape={ButtonShape.Rectangle}
      onClick={onChangeSelection}
      bgColorClassName='v2-bg-transparent'
    >
      Change selection
    </Button>
  );
};

export enum Tabs {
  All = 'all',
  Tags = 'tags',
}

export enum DisplayOrder {
  Random = 'random',
  Manual = 'manual',
}

const TagsToggleItem: ReactNode = (
  <div>
    <FontAwesomeIcon icon={faTag} />
    <span className='ml-2.5 font-bold'>Tags</span>
  </div>
);

const AllToggleItem: ReactNode = (
  <div>
    <FontAwesomeIcon icon={faFolder} />
    <span className='ml-2.5 font-bold'>All</span>
  </div>
);

interface ChangeDisplayOrderButtonProps {
  disabled?: boolean;
  onClick?: () => void;
}

const ChangeDisplayOrderButton = ({
  onClick,
  disabled,
}: ChangeDisplayOrderButtonProps) => (
  <Button
    variant={disabled ? ButtonVariant.Disabled : ButtonVariant.SecondaryWhite}
    className='mt-5'
    onClick={(!disabled && onClick) || undefined}
    disableHover={disabled}
    disabled={disabled}
    bgColorClassName={cx({
      'v2-bg-neutral-600': disabled,
    })}
  >
    Change order
  </Button>
);

export interface TagsDropDownProps {
  selectedTag?: string;
  setSelectedTag: (tag: string) => void;
}

const TagsDropdown = ({ selectedTag, setSelectedTag }: TagsDropDownProps) => {
  const { data: tags = [] } = useTags();

  const items = useMemo(
    () =>
      tags.reduce<Record<string, React.ReactNode>>((items, tag) => {
        return {
          ...items,
          [tag.uuid]: <span className='text-base p-0 m-0'>{tag.name}</span>,
        };
      }, {}),
    [tags]
  );

  return (
    <DropdownSelector
      items={items}
      bodyPlaceholder='Choose a tag'
      type={SelectorType.Select}
      bodyContainerClassName='!w-full'
      dropdownProps={{
        width: '100%',
      }}
      className='mt-5 v2-text-black z-10 w-full'
      bodyLabelClassName='text-base'
      onSelect={(tag) => {
        setSelectedTag(tag[0]);
      }}
      selected={selectedTag != null ? [selectedTag] : []}
    />
  );
};

export default function Select() {
  const [showingSelectModal, setShowingSelectModal] = useState<boolean>(false);

  const [showingOrderTestimonialsModal, setShowingOrderTestimonialsModal] =
    useState(false);

  const { publish, totalAssets = 0 } = usePublishPageContext();

  const [{ type, tag, displayOrder, ...state }, { set, done }] =
    usePublishEditorState();

  const queryClient = useQueryClient();
  const analytics = useAnalytics();

  const { mutate: updatePublishableMutation } = useUpdatePublishable({
    onSuccess: () => {
      done();

      queryClient.invalidateQueries(queryKeys.all);
    },
  });

  const onTagSelect = (tag: string) => {
    set('tag', tag);

    updatePublishableMutation({
      type,
      uuid: publish.uuid,
      tag_uuid: tag,
      metadata: {
        ...parsePublishStateToMetadata(state),
        display_order: displayOrder,
        order: state.order,
      },
    });

    analytics.track(TESTIMONIAL_PUBLISH_SELECT_TESTIMONIALS_FILTER, {
      publishType: type,
    });
  };

  const { setSelectedTags, clearSelectedTags } = useFilteredTags();

  const ToggleSelectorItems: Record<Tabs, React.ReactNode> = {
    [Tabs.Tags]: TagsToggleItem,
    [Tabs.All]: AllToggleItem,
  };

  const testimonialCount = publish.publishable_resources?.length ?? 0;

  const [selectedTab, setSelectedTab] = useState<Tabs>(() => {
    if (tag != null) {
      return Tabs.Tags;
    }

    return testimonialCount > 0 ? Tabs.All : Tabs.Tags;
  });

  useEffect(() => {
    if (publish.tag && selectedTab == Tabs.Tags) {
      setSelectedTags([publish.tag]);
    } else {
      clearSelectedTags();
    }
  }, [publish?.tag, selectedTab]);

  const onSwitchTab = (tab: Tabs) => {
    if (tab === selectedTab) return;

    setSelectedTab(tab);

    clearSelectedTags();

    // Clear selected assets and tag whenever switching from All to Tags tab.
    if (tab === Tabs.Tags) {
      set('tag', undefined);

      updatePublishableMutation({
        type,
        uuid: publish.uuid,
        tag_uuid: null,
        publishable_resources_uuids: [],
        get_publishable_resources: true,
      });
    }
  };

  const onSelect = () => {
    setShowingSelectModal(true);
  };

  const showSelectButton = useMemo(
    () => testimonialCount === 0 && selectedTab !== Tabs.Tags,
    [testimonialCount, selectedTab]
  );

  const showChangeSelectionButton = useMemo(
    () => testimonialCount > 0,
    [testimonialCount]
  );

  const showTestimonialCount = useMemo(
    () => tag != null || testimonialCount > 0,
    [tag, testimonialCount]
  );

  const assetSelectionText = useMemo(() => {
    const currentAssets = publish.publishable_resources?.length ?? 0;
    const testimonialText = currentAssets > 1 ? 'testimonials' : 'testimonial';

    if (currentAssets > 0) {
      if (selectedTab === Tabs.All) {
        return `${currentAssets} ${testimonialText} selected`;
      }

      if (selectedTab === Tabs.Tags) {
        return `${currentAssets} ${testimonialText} selected from ${totalAssets} tagged`;
      }
    }
  }, [publish?.publishable_resources, tag, selectedTab]);

  const onSelectButtonClick = () => {
    onSelect();
    analytics.track(TESTIMONIAL_PUBLISH_SELECT_TESTIMONIALS, {
      publishType: type,
    });
  };

  const onSelectionChangeClick = () => {
    onSelect();
    analytics.track(TESTIMONIAL_PUBLISH_SELECT_CHANGE_SELECTION, {
      publishType: type,
    });
  };

  const onChangeOrderClick = () => {
    setShowingOrderTestimonialsModal(false);
    analytics.track(TESTIMONIAL_PUBLISH_SELECT_CHANGE_ORDER, {
      publishType: type,
    });
  };

  return (
    <>
      <p className='text-[21px] text-white font-bold p-0 m-0'>
        Select testimonials
      </p>
      <p className='text-base mt-5'>Select from</p>
      <div className='mt-2.5 h-10'>
        <ToggleSelector
          items={ToggleSelectorItems}
          onSelect={(tab) => onSwitchTab(tab as Tabs)}
          selected={selectedTab}
          shape={ToggleSelectorShape.Rectangle}
          textColorClassName='v2-text-white'
          notSelectedBgColorClassName='v2-bg-neutral-600'
          textHoverColorClassName='v2-text-black'
          selectedTextColorClassName='v2-text-black'
          size='md'
          buttonBorderColorClassName='!border-none'
          className='v2-bg-yellow-300 !v2-rounded-md overflow-auto'
        />
      </div>

      {selectedTab === Tabs.Tags && (
        <div className='z-20 h-[66px]'>
          <TagsDropdown selectedTag={tag} setSelectedTag={onTagSelect} />
        </div>
      )}

      {assetSelectionText && (
        <div
          className={cx(
            'text-center text-base v2-text-white font-bold p-0 mt-2.5 mb-3.5 flex items-center justify-center',
            {
              hidden: !showTestimonialCount,
            }
          )}
        >
          {assetSelectionText}
        </div>
      )}

      {showChangeSelectionButton && (
        <ChangeSelectionButton onChangeSelection={onSelectionChangeClick} />
      )}

      {showSelectButton && <SelectButton onSelect={onSelectButtonClick} />}

      <p className='text-[21px] text-white font-bold p-0 mt-[30px] mb-0'>
        Display order
      </p>

      <div className='mt-5'>
        <Checkbox
          label='Random'
          name='random'
          checked={displayOrder === DisplayOrder.Random}
          disabled={!testimonialCount}
          onChange={() => set('displayOrder', DisplayOrder.Random)}
          bgColorClassName={
            !testimonialCount ? 'v2-bg-neutral-100' : 'v2-bg-white'
          }
          labelTextColorClassName={
            !testimonialCount ? 'v2-text-neutral-500' : 'v2-text-white'
          }
          labelTextSizeClassName='text-base'
          className='font-normal'
        />
        <Checkbox
          label='Manually'
          name='manual'
          checked={displayOrder === DisplayOrder.Manual}
          disabled={!testimonialCount}
          onChange={() => set('displayOrder', DisplayOrder.Manual)}
          bgColorClassName={
            !testimonialCount ? 'v2-bg-neutral-100' : 'v2-bg-white'
          }
          labelTextColorClassName={
            !testimonialCount ? 'v2-text-neutral-500' : 'v2-text-white'
          }
          labelTextSizeClassName='v2-text-base'
          className='font-normal mt-[15px]'
        />
      </div>

      <ChangeDisplayOrderButton
        disabled={!testimonialCount || displayOrder !== DisplayOrder.Manual}
        onClick={() => setShowingOrderTestimonialsModal(true)}
      />

      {showingSelectModal && (
        <PublishSelectModalContextProvider>
          <PublishModal
            uuid={publish.uuid}
            open={showingSelectModal}
            closeModal={() => {
              setShowingSelectModal(false);
            }}
          />
        </PublishSelectModalContextProvider>
      )}

      <OrderTestimonialsModal
        open={showingOrderTestimonialsModal}
        onModalClose={onChangeOrderClick}
      />
    </>
  );
}
